import moment from 'moment';

import {FullLengthDateRange} from 'common/widgets/date-range-picker/models/full-length-date-range';
import {
  END_OF_TODAY,
  END_OF_YESTERDAY,
  START_OF_TODAY,
  START_OF_YESTERDAY,
} from 'common/widgets/date-range-picker/models/presets';
import {DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';

/**
 * Converts a FullLengthDateRange object into relative dates for creating a "classic" DateRange
 * @return Relative from/to
 */
export const fullLengthDateRangeToRelativeDates = (fullLengthDateRange) => {
  const {from, to} = fullLengthDateRange.getCalendarDates();
  return {
    relativeFrom: moment(from).diff(moment(), `day`),
    relativeTo: moment(to).diff(moment(), `day`),
  };
};

/**
 * Converts a FullLengthDateRange bookmark into relative dates for creating a "classic" DateRange
 * @return Relative from/to
 */
export const fullLengthDateRangeBookmarkToRelativeDates = (fullLengthDateRange) => {
  return fullLengthDateRangeToRelativeDates(FullLengthDateRange.fromBookmarkFormat(fullLengthDateRange));
};

/**
 * Converts relative from and to dates from a "classic" DateRange into a rolling FullLengthDateRange
 * @return FullLengthDateRange
 */
const relativeDatesToRollingFullLengthDateRange = ({relativeFrom, relativeTo}) => {
  const fullLengthDateRange = {type: DateRangeType.Between};

  fullLengthDateRange.from = {unit: TimeUnit.Day, value: -relativeFrom};
  fullLengthDateRange.to = {unit: TimeUnit.Day, value: -relativeTo};
  fullLengthDateRange.rolling = true;
  if (relativeFrom === relativeTo) {
    fullLengthDateRange.type = DateRangeType.On;
  }

  return fullLengthDateRange;
};

/**
 * Converts relative from and to dates from a "classic" DateRange into the corresponding FullLengthDateRange
 * @return FullLengthDateRange
 */
export const relativeDatesToFullLengthDateRange = ({relativeFrom, relativeTo}) => {
  const fullLengthDateRange = {type: DateRangeType.Between};

  if (relativeTo === 0) {
    // End date is today
    if (relativeFrom === 0) {
      // If start date is also today, set date range to "Current Day"
      fullLengthDateRange.from = START_OF_TODAY;
      fullLengthDateRange.to = END_OF_TODAY;
    } else {
      // Otherwise, "In the last"
      fullLengthDateRange.type = DateRangeType.RelativeAfter;
      fullLengthDateRange.window = {unit: TimeUnit.Day, value: -relativeFrom};
    }
  } else if (relativeFrom === -1 && relativeTo === -1) {
    // Start and end dates are yesterday, set date range to "Previous Day"
    fullLengthDateRange.from = START_OF_YESTERDAY;
    fullLengthDateRange.to = END_OF_YESTERDAY;
  } else {
    // rolling between/on
    return relativeDatesToRollingFullLengthDateRange({relativeFrom, relativeTo});
  }

  return fullLengthDateRange;
};

/**
 * Converts relative from and to dates from a "classic" DateRange into the corresponding rolling FullLengthDateRange bookmark
 * @return FullLengthDateRange bookmark
 */
export const relativeDatesToFullLengthDateRangeRollingBettweenBookmark = (fullLengthDateRange) => {
  return new FullLengthDateRange(relativeDatesToRollingFullLengthDateRange(fullLengthDateRange)).getBookmarkFormat();
};
